import React from "react";
import { Box, Typography } from "@material-ui/core";
import FavoriteRoundedIcon from "@material-ui/icons/FavoriteRounded";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import useCommonStyles from "./style";

// Map a finding's status to an accent colour: green when in range, red when the
// value is out of range / clinically flagged. Anything else falls back to amber.
const statusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (/(normal|optimal|good|balanced|healthy|in range)/.test(s)) return "#0a8f54";
  if (/(low|high|critical|severe|elevated|deficien|abnormal|border)/.test(s))
    return "#c0392b";
  return "#d9772e";
};

/**
 * AI-highlighted lab findings from the Clinical Blood Report (`labFindings`).
 * Read-only. Each finding shows the test + reference range, the patient value &
 * status (colour-coded), the clinical meaning, and the Ayurvedic correlation.
 * The report's `icon` field is a PDF-only asset path, so we render a consistent
 * vital icon (tinted by status) instead of trying to load the missing image.
 */
const LabFindings = ({ findings }) => {
  const classes = useCommonStyles();

  if (!Array.isArray(findings) || findings.length === 0) {
    return (
      <Box className={classes.hiPlaceholder}>
        <InfoOutlinedIcon className={classes.hiPlaceholderIcon} />
        <Typography className={classes.hiPlaceholderNote}>
          No lab findings available for this patient yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.labFindingsWrap}>
      {findings.map((finding, index) => {
        const color = statusColor(finding.status);
        return (
          <Box key={index} className={classes.labFindingRow}>
            <Box className={classes.labFindingHead}>
              <FavoriteRoundedIcon
                className={classes.labFindingIcon}
                style={{ color }}
              />
              <Box minWidth={0}>
                <Typography className={classes.labFindingTest}>
                  {finding.test}
                </Typography>
                {finding.idealRange && (
                  <Typography className={classes.labFindingRange}>
                    Range: {finding.idealRange}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box className={classes.labFindingValueBox}>
              <Typography className={classes.labFindingValue} style={{ color }}>
                {finding.yourValue}
              </Typography>
              {finding.status && (
                <Typography
                  className={classes.labFindingStatus}
                  style={{ color }}
                >
                  {finding.status}
                </Typography>
              )}
            </Box>

            <Typography className={classes.labFindingMeaning}>
              {finding.meaning}
            </Typography>

            {finding.ayurvedicCorrelation && (
              <Typography className={classes.labFindingAyur}>
                {finding.ayurvedicCorrelation}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default LabFindings;
